// ============================================================
//  CLOUD API — Unified data layer
//  File: src/lib/cloudApi.js
//
//  Priority:
//    1. Supabase (production cloud) — if env vars set
//    2. Legacy backend (localhost dev) — fallback
//    3. Static fallback data — if both fail
//
//  This means:
//    - Vercel production → reads/writes Supabase
//    - Local dev → can use backend OR Supabase
//    - No backend? Static data shown, no crash
// ============================================================

import {
  supabase, isSupabaseReady, uploadToSupabase,
  TABLES, BUCKET,
  dbGet, dbGetOne, dbUpsert, dbInsert, dbUpdate, dbDelete,
} from "./supabase";
import { api, uploadFile as legacyUpload } from "./api";
import { STATIC_FALLBACK } from "@/data/staticFallback";
import { getUploadErrorMessage } from "@/lib/adminImageUtils";

// ── Determine mode ────────────────────────────────────────────
const MODE = isSupabaseReady() ? "supabase" : "backend";
console.info(`[CloudAPI] Mode: ${MODE}`);

export function normalizeGallery(value) {
  if (!value) return [];
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      return normalizeGallery(JSON.parse(trimmed));
    } catch {
      return [trimmed];
    }
  }
  if (Array.isArray(value)) {
    return [...new Set(value.flatMap((item) => normalizeGallery(item)).filter(Boolean))];
  }
  if (typeof value === "object") {
    if (typeof value.url === "string") return normalizeGallery(value.url);
    return normalizeGallery(Object.values(value));
  }
  return [];
}

export function getWeddingGallery(wedding) {
  if (!wedding || typeof wedding !== "object") return [];

  for (const field of ["gallery", "images", "photos", "album"]) {
    const images = normalizeGallery(wedding[field]);
    if (images.length) return images;
  }

  return [];
}

export function normalizeWedding(wedding, fallback = {}) {
  const source = { ...fallback, ...(wedding || {}) };
  const sourceGallery = getWeddingGallery(wedding);
  const fallbackGallery = getWeddingGallery(fallback);
  const gallery = sourceGallery.length ? sourceGallery : fallbackGallery;
  const cover = typeof source.cover === "string" && source.cover.trim()
    ? source.cover.trim()
    : typeof fallback.cover === "string" && fallback.cover.trim()
      ? fallback.cover.trim()
      : gallery[0] || "";

  return {
    ...source,
    slug: source.slug || fallback.slug || "",
    couple: source.couple || fallback.couple || "Wedding Story",
    location: source.location || fallback.location || "Gujarat, India",
    type: source.type || fallback.type || "Wedding",
    story: source.story || fallback.story || "",
    fullStory: source.fullStory || fallback.fullStory || source.story || fallback.story || "",
    cover,
    coverPositionDesktop: source.coverPositionDesktop || source.cover_position_desktop || "center center",
    coverPositionMobile: source.coverPositionMobile || source.cover_position_mobile || "center center",
    coverFit: source.coverFit || source.cover_fit || "cover",
    gallery,
  };
}

function weddingFallback(slug) {
  return STATIC_FALLBACK.weddings.find((item) => item.slug === slug) || { slug };
}

// ── SETTINGS ─────────────────────────────────────────────────
export async function getSettings() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.settings);
      if (!rows || rows.length === 0) return STATIC_FALLBACK.settings;
      // Convert rows [{key, value}] to object
      return Object.fromEntries(rows.map(r => [r.key, r.value]));
    } catch { return STATIC_FALLBACK.settings; }
  }
  try { const { data } = await api.get("/settings"); return data; }
  catch { return STATIC_FALLBACK.settings; }
}

export async function saveSettings(obj) {
  if (MODE === "supabase") {
    const rows = Object.entries(obj).map(([key, value]) => ({ key, value }));
    for (const row of rows) {
      await supabase.from(TABLES.settings).upsert(row, { onConflict: "key" });
    }
    return obj;
  }
  const { data } = await api.put("/admin/settings", obj);
  return data;
}

// ── WEDDINGS ─────────────────────────────────────────────────
export async function getWeddings() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.weddings);
      return rows?.length
        ? rows.map((row) => normalizeWedding(row, weddingFallback(row.slug))).sort((a,b) => (a.order||0)-(b.order||0))
        : STATIC_FALLBACK.weddings.map((row) => normalizeWedding(row));
    } catch { return STATIC_FALLBACK.weddings.map((row) => normalizeWedding(row)); }
  }
  try {
    const { data } = await api.get("/weddings");
    return (data || []).map((row) => normalizeWedding(row, weddingFallback(row.slug)));
  } catch {
    return STATIC_FALLBACK.weddings.map((row) => normalizeWedding(row));
  }
}

export async function getWedding(slug) {
  if (MODE === "supabase") {
    try {
      const row = await dbGetOne(TABLES.weddings, "slug", slug);
      return row ? normalizeWedding(row, weddingFallback(slug)) : null;
    } catch {
      const fallback = STATIC_FALLBACK.weddings.find((item) => item.slug === slug);
      return fallback ? normalizeWedding(fallback) : null;
    }
  }
  try {
    const { data } = await api.get(`/weddings/${slug}`);
    return normalizeWedding(data, weddingFallback(slug));
  } catch {
    const fallback = STATIC_FALLBACK.weddings.find((item) => item.slug === slug);
    return fallback ? normalizeWedding(fallback) : null;
  }
}

export async function saveWedding(wedding) {
  const normalized = normalizeWedding(wedding);
  if (MODE === "supabase") {
    const row = {
      ...normalized,
      gallery: normalizeGallery(normalized.gallery),
      cover_position_desktop: normalized.coverPositionDesktop,
      cover_position_mobile: normalized.coverPositionMobile,
      cover_fit: normalized.coverFit,
      updated_at: new Date().toISOString(),
    };
    delete row.coverPositionDesktop;
    delete row.coverPositionMobile;
    delete row.coverFit;
    if (!row.id) row.id = crypto.randomUUID();
    return await dbUpsert(TABLES.weddings, row, "slug");
  }
  if (normalized.id) {
    const { data } = await api.put(`/admin/weddings/${normalized.slug}`, normalized);
    return data;
  }
  const { data } = await api.post("/admin/weddings", normalized);
  return data;
}

export async function deleteWedding(slug) {
  if (MODE === "supabase") return await dbDelete(TABLES.weddings, "slug", slug);
  await api.delete(`/admin/weddings/${slug}`);
}

// ── FILMS ─────────────────────────────────────────────────────
export async function getFilms() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.films);
      return rows?.length ? rows.sort((a,b)=>(a.order||0)-(b.order||0)) : STATIC_FALLBACK.films;
    } catch { return STATIC_FALLBACK.films; }
  }
  try { const { data } = await api.get("/films"); return data; }
  catch { return STATIC_FALLBACK.films; }
}

export async function saveFilm(film) {
  if (MODE === "supabase") {
    const row = { ...film, updated_at: new Date().toISOString() };
    if (!row.id) row.id = crypto.randomUUID();
    return await dbUpsert(TABLES.films, row, "id");
  }
  if (film.id) { const { data } = await api.put(`/admin/films/${film.id}`, film); return data; }
  const { data } = await api.post("/admin/films", film);
  return data;
}

export async function deleteFilm(id) {
  if (MODE === "supabase") return await dbDelete(TABLES.films, "id", id);
  await api.delete(`/admin/films/${id}`);
}

// ── PRE-WEDDING ───────────────────────────────────────────────
export async function getPrewedding() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.prewedding);
      return rows?.length ? rows.sort((a,b)=>(a.order||0)-(b.order||0)) : STATIC_FALLBACK.prewedding;
    } catch { return STATIC_FALLBACK.prewedding; }
  }
  try { const { data } = await api.get("/prewedding"); return data; }
  catch { return STATIC_FALLBACK.prewedding; }
}

export async function savePreweddingImage(item) {
  if (MODE === "supabase") {
    const row = { ...item, updated_at: new Date().toISOString() };
    if (!row.id) row.id = crypto.randomUUID();
    return await dbUpsert(TABLES.prewedding, row, "id");
  }
  if (item.id) { const { data } = await api.put(`/admin/prewedding/${item.id}`, item); return data; }
  const { data } = await api.post("/admin/prewedding", item);
  return data;
}

export async function deletePreweddingImage(id) {
  if (MODE === "supabase") return await dbDelete(TABLES.prewedding, "id", id);
  await api.delete(`/admin/prewedding/${id}`);
}

// ── PRE-WEDDING SESSIONS (album/session model) ───────────────
// Mirrors the wedding session shape so /pre-wedding can list named
// couples/sessions and /pre-wedding/:slug can show a full album.
export function normalizePreweddingSession(row, fallback = {}) {
  const source = { ...fallback, ...(row || {}) };
  const gallery = normalizeGallery(source.gallery);
  const cover = (typeof source.cover === "string" && source.cover.trim())
    ? source.cover.trim()
    : gallery[0] || "";
  return {
    ...source,
    slug: source.slug || "",
    couple: source.couple || "Pre-Wedding Session",
    location: source.location || "",
    date: source.date || "",
    type: source.type || "Pre-Wedding",
    story: source.story || "",
    fullStory: source.fullStory || source.full_story || source.story || "",
    cover,
    coverPositionDesktop: source.coverPositionDesktop || source.cover_position_desktop || "center center",
    coverPositionMobile: source.coverPositionMobile || source.cover_position_mobile || "center center",
    coverFit: source.coverFit || source.cover_fit || "cover",
    gallery,
    featured: Boolean(source.featured),
    status: source.status || "published",
    order: source.order ?? source["order"] ?? 0,
  };
}

export async function getPreweddingSessions() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.preweddingSessions);
      if (!rows?.length) return [];
      return rows
        .map((r) => normalizePreweddingSession(r))
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    } catch { return []; }
  }
  try { const { data } = await api.get("/prewedding-sessions"); return (data || []).map(normalizePreweddingSession); }
  catch { return []; }
}

export async function getPreweddingSession(slug) {
  if (MODE === "supabase") {
    try {
      const row = await dbGetOne(TABLES.preweddingSessions, "slug", slug);
      return row ? normalizePreweddingSession(row) : null;
    } catch { return null; }
  }
  try { const { data } = await api.get(`/prewedding-sessions/${slug}`); return normalizePreweddingSession(data); }
  catch { return null; }
}

export async function savePreweddingSession(session) {
  const normalized = normalizePreweddingSession(session);
  if (MODE === "supabase") {
    const row = {
      ...normalized,
      gallery: normalizeGallery(normalized.gallery),
      full_story: normalized.fullStory,
      cover_position_desktop: normalized.coverPositionDesktop,
      cover_position_mobile: normalized.coverPositionMobile,
      cover_fit: normalized.coverFit,
      updated_at: new Date().toISOString(),
    };
    delete row.fullStory;
    delete row.coverPositionDesktop;
    delete row.coverPositionMobile;
    delete row.coverFit;
    if (!row.id) row.id = crypto.randomUUID();
    return await dbUpsert(TABLES.preweddingSessions, row, "slug");
  }
  if (normalized.id) { const { data } = await api.put(`/admin/prewedding-sessions/${normalized.slug}`, normalized); return data; }
  const { data } = await api.post("/admin/prewedding-sessions", normalized);
  return data;
}

export async function deletePreweddingSession(slug) {
  if (MODE === "supabase") return await dbDelete(TABLES.preweddingSessions, "slug", slug);
  await api.delete(`/admin/prewedding-sessions/${slug}`);
}

// ── INSTAGRAM ─────────────────────────────────────────────────
export async function getInstagram() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.instagram);
      return rows?.length ? rows.sort((a,b)=>(a.order||0)-(b.order||0)) : [];
    } catch { return []; }
  }
  try { const { data } = await api.get("/instagram"); return data; }
  catch { return []; }
}

export async function saveInstagramItem(item) {
  if (MODE === "supabase") {
    const row = { ...item, updated_at: new Date().toISOString() };
    if (!row.id) row.id = crypto.randomUUID();
    return await dbUpsert(TABLES.instagram, row, "id");
  }
  if (item.id) { const { data } = await api.put(`/admin/instagram/${item.id}`, item); return data; }
  const { data } = await api.post("/admin/instagram", item);
  return data;
}

export async function deleteInstagramItem(id) {
  if (MODE === "supabase") return await dbDelete(TABLES.instagram, "id", id);
  await api.delete(`/admin/instagram/${id}`);
}

// ── TESTIMONIALS ──────────────────────────────────────────────
export async function getTestimonials() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.testimonials);
      return rows?.length ? rows : STATIC_FALLBACK.testimonials;
    } catch { return STATIC_FALLBACK.testimonials; }
  }
  try { const { data } = await api.get("/testimonials"); return data; }
  catch { return STATIC_FALLBACK.testimonials; }
}

export async function saveTestimonial(item) {
  if (MODE === "supabase") {
    const row = { ...item, updated_at: new Date().toISOString() };
    if (!row.id) row.id = crypto.randomUUID();
    return await dbUpsert(TABLES.testimonials, row, "id");
  }
  if (item.id) { const { data } = await api.put(`/admin/testimonials/${item.id}`, item); return data; }
  const { data } = await api.post("/admin/testimonials", item);
  return data;
}

export async function deleteTestimonial(id) {
  if (MODE === "supabase") return await dbDelete(TABLES.testimonials, "id", id);
  await api.delete(`/admin/testimonials/${id}`);
}

// ── INQUIRIES ─────────────────────────────────────────────────
export async function submitInquiry(form) {
  if (MODE === "supabase") {
    const row = { ...form, id: crypto.randomUUID(), created_at: new Date().toISOString(), status: "new" };
    await dbInsert(TABLES.inquiries, row);
    return row;
  }
  const { data } = await api.post("/inquiries", form);
  return data;
}

export async function getInquiries() {
  if (MODE === "supabase") {
    try {
      const rows = await dbGet(TABLES.inquiries);
      return rows?.sort((a,b) => new Date(b.created_at) - new Date(a.created_at)) || [];
    } catch { return []; }
  }
  try { const { data } = await api.get("/admin/inquiries"); return data; }
  catch { return []; }
}

export async function deleteInquiry(id) {
  if (MODE === "supabase") return await dbDelete(TABLES.inquiries, "id", id);
  await api.delete(`/admin/inquiries/${id}`);
}

// ── FILE UPLOAD ───────────────────────────────────────────────
export async function uploadMedia(file, folder = "uploads", onProgress) {
  try {
    if (MODE === "supabase") {
      // Simulate progress for UX. Supabase storage currently does not expose browser upload progress.
      onProgress?.(30);
      const url = await uploadToSupabase(file, folder);
      onProgress?.(100);
      return { url };
    }
    return await legacyUpload(file, onProgress);
  } catch (error) {
    const message = getUploadErrorMessage(error);
    console.error("[ADMIN_UPLOAD_MEDIA_ERROR]", {
      mode: MODE,
      folder,
      fileName: file?.name,
      fileType: file?.type,
      fileSize: file?.size,
      message,
      error,
    });
    throw new Error(message);
  }
}
